"use client";

import { t } from "@/lib/i18n";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { CheckCircle, XCircle, AlertCircle, Info, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

interface ToastProps {
  message: string;
  type: "success" | "error" | "warning" | "info";
  onClose: () => void;
  duration?: number;
}

export default function Toast({
  message,
  type,
  onClose,
  duration = 3000,
}: ToastProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    const timer = setTimeout(() => {
      onClose();
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  // Hindari mismatch SSR (Server-Side Rendering)
  if (!mounted) return null;

  const config = {
    success: {
      icon: <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-500 shrink-0" />,
      border: "border-emerald-200/80",
      accentBar: "bg-emerald-500",
      text: "text-emerald-950",
    },
    error: {
      icon: <XCircle className="w-5 h-5 sm:w-6 sm:h-6 text-rose-500 shrink-0" />,
      border: "border-rose-200/80",
      accentBar: "bg-rose-500",
      text: "text-rose-950",
    },
    warning: {
      icon: <AlertCircle className="w-5 h-5 sm:w-6 sm:h-6 text-amber-500 shrink-0" />,
      border: "border-amber-200/80",
      accentBar: "bg-amber-500",
      text: "text-amber-950",
    },
    info: {
      icon: <Info className="w-5 h-5 sm:w-6 sm:h-6 text-blue-500 shrink-0" />,
      border: "border-blue-200/80",
      accentBar: "bg-blue-500",
      text: "text-blue-950",
    },
  };

  const current = config[type];

  // Menggunakan Portal agar keluar dari DOM modal/halaman manapun
  const toastContent = (
    <div className="fixed inset-x-0 top-4 sm:top-6 z-[9999] flex justify-center px-4 pointer-events-none">
      <motion.div
        role={type === "error" || type === "warning" ? "alert" : "status"}
        initial={{ opacity: 0, y: -20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -20, scale: 0.95 }}
        transition={{ type: "spring", damping: 25, stiffness: 350 }}
        className={`pointer-events-auto flex items-center gap-3 sm:gap-4 px-4 sm:px-5 py-3.5 sm:py-4 rounded-2xl shadow-2xl border backdrop-blur-2xl bg-white/90 ${current.border} w-full max-w-[92%] sm:max-w-md lg:max-w-lg overflow-hidden relative`}
      >
        {/* Garis Aksen Samping (Glow Bar) */}
        <div className={`absolute top-0 bottom-0 left-0 w-1.5 ${current.accentBar}`} />

        {current.icon}
        
        <div className="flex-1 pr-1">
          <p className={`text-xs sm:text-sm font-bold tracking-tight leading-relaxed ${current.text}`}>
            {t(message)}
          </p>
        </div>

        <button
          onClick={onClose}
          className="p-1.5 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-200/60 transition-colors shrink-0"
        >
          <X size={16} />
        </button>
      </motion.div>
    </div>
  );

  return createPortal(toastContent, document.body);
}